import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Spin,
  Table,
  Typography,
  message,
} from "antd";
import { AppContext } from "../context/AppContext";
import DoubleEntryService, { STANDARD_ACCOUNTS } from "../utils/doubleEntryService";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const formatCurrency = (value) =>
  `ZMW ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const GeneralLedger = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("1000"); // Default to Cash
  const [dateRange, setDateRange] = useState([
    moment().startOf("month"),
    moment(),
  ]);

  useEffect(() => {
    if (selectedAccount) {
      fetchGeneralLedger();
    }
  }, [company?.id, selectedAccount, dateRange]);

  const fetchGeneralLedger = async () => {
    if (!company?.id || !token || !selectedAccount) return;

    setLoading(true);
    try {
      const [startDate, endDate] = dateRange;

      const data = await DoubleEntryService.getGeneralLedger({
        company: company.id,
        token,
        accountCode: selectedAccount,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      });

      setLedgerData(data);
    } catch (error) {
      console.error("Error fetching general ledger:", error);
      message.error("Failed to load general ledger");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Debit",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      width: 130,
      render: (value) => (value > 0 ? formatCurrency(value) : "-"),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      width: 130,
      render: (value) => (value > 0 ? formatCurrency(value) : "-"),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      width: 150,
      render: (value) => (
        <Text strong style={{ color: value >= 0 ? "#52c41a" : "#ff4d4f" }}>
          {formatCurrency(Math.abs(value))} {value < 0 ? "CR" : "DR"}
        </Text>
      ),
    },
  ];

  const accountOptions = Object.values(STANDARD_ACCOUNTS).map((account) => ({
    label: `${account.code} - ${account.name}`,
    value: account.code,
    type: account.type,
  }));

  const selectedAccountInfo = Object.values(STANDARD_ACCOUNTS).find(
    (acc) => acc.code === selectedAccount
  );

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={3} style={{ marginBottom: 0 }}>
            General Ledger
          </Title>
          <Text type="secondary">
            Detailed transaction history for individual accounts
          </Text>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card size="small">
            <Text type="secondary">Select Account</Text>
            <Select
              value={selectedAccount}
              onChange={setSelectedAccount}
              style={{ width: "100%", marginTop: 8 }}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {accountOptions.map((option) => (
                <Option key={option.value} value={option.value} label={option.label}>
                  {option.label}
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    ({option.type})
                  </Text>
                </Option>
              ))}
            </Select>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small">
            <Text type="secondary">Date Range</Text>
            <RangePicker
              value={dateRange}
              onChange={(range) => range && setDateRange(range)}
              style={{ width: "100%", marginTop: 8 }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          selectedAccountInfo && (
            <Text strong>
              {selectedAccountInfo.code} - {selectedAccountInfo.name}
              <Text type="secondary" style={{ marginLeft: 12, fontWeight: "normal" }}>
                ({selectedAccountInfo.type})
              </Text>
            </Text>
          )
        }
      >
        <Spin spinning={loading}>
          <Table
            dataSource={ledgerData}
            columns={columns}
            rowKey={(record, index) => `${record.date}-${index}`}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} entries`,
            }}
          />

          {!loading && ledgerData.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Text type="secondary">
                No transactions found for this account in the selected period.
              </Text>
            </div>
          )}
        </Spin>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Title level={5}>Understanding the General Ledger</Title>
        <Text>
          The general ledger shows all transactions that affect a specific account:
        </Text>
        <ul>
          <li>
            <Text strong>Debit</Text> - Increases to asset/expense accounts,
            decreases to liability/equity/revenue accounts
          </li>
          <li>
            <Text strong>Credit</Text> - Decreases to asset/expense accounts,
            increases to liability/equity/revenue accounts
          </li>
          <li>
            <Text strong>Balance</Text> - Running balance after each transaction
          </li>
          <li>
            <Text strong>DR (Debit Balance)</Text> - Typical for assets and
            expenses
          </li>
          <li>
            <Text strong>CR (Credit Balance)</Text> - Typical for liabilities,
            equity, and revenue
          </li>
        </ul>
      </Card>
    </div>
  );
};

export default GeneralLedger;
